import SearchHelpModal from './dashboard/DashboardSearchHelp-v1';
import DashboardSearchControls from './dashboard/DashboardSearchControls-v1';
import DashboardFilterChips from './dashboard/DashboardFilterChips-v1';
import DashboardPipelineSection from './dashboard/DashboardPipelineSection-v1';
import DashboardStatusSection from './dashboard/DashboardStatusSection-v1';
import Skeleton from './dashboard/DashboardSkeleton-v1';
import { canUseOutreachPacks } from '../lib/outreachPack';
import { relatedAddressGroups } from '../lib/daDisplay';
import { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import DACard from './DACard';
import HelpTip from './HelpTip';
import PipelineOutcomesCard from './PipelineOutcomesCard';
import AskSiteScope from './AskSiteScope';
import DATableView from './DATableView';
import { useIsMobile } from '../hooks/useIsMobile';
import FilterDrawer from './ui/FilterDrawer';
import TradePicker from './ui/TradePicker';
import { matchTradeOpportunity, TRADES } from '../lib/tradeOpportunities';
import { DA_CATEGORIES, resolveProposalType } from '../lib/daCategories';
import { PRICE_RANGE_DEFS } from '../lib/constants';
import { sStyle, sAddr, fetchDADetailRow } from '../lib/utils';
import {
  formatPipelineMoney,
  getPipelineCost,
  pipelineValueQualifier,
  summarisePipelineValue,
} from '../lib/pipelineCost';
import { shortCouncilName } from '../config/registry';
import { ALL_COUNCILS, ALL_STATUS_KEYS } from './dashboard/dashboardFilterOptions-v1';

const STATUS_GROUPS = {
  'Lodged': ['Lodged', 'Submitted', 'Open', 'In Progress', 'Pending Lodgement'],
  'Under Assessment': ['Under Assessment'],
  'On Exhibition': ['On Exhibition'],
  'Approved': ['Approved', 'Determined', 'Deferred Commencement'],
  'Rejected': ['Rejected', 'Refused', 'CDC Rejected'],
};
const CDC_TYPES = ['Complying Development', 'Complying Development Cert'];

function isActiveExhibition(da) {
  const start = da.exhibition_start || da.exhibition_start_date;
  const end = da.exhibition_end || da.exhibition_end_date;
  if (!start || !end) return false;
  const startDate = new Date(start);
  const endDate = new Date(end);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return false;
  const now = new Date();
  return startDate <= now && endDate > now;
}

function matchesCouncilFilter(da, councils) {
  if (!Array.isArray(councils) || councils.length === 0) return false;
  return councils.some(c => (da.council || '').toLowerCase().includes(String(c).toLowerCase()));
}

function matchesStatusFilter(da, stFilt) {
  if (!Array.isArray(stFilt) || stFilt.length === 0) return false;
  if (stFilt.includes('On Exhibition') && (isActiveExhibition(da) || da.status === 'On Exhibition')) return true;
  const otherKeys = stFilt.filter(k => k !== 'On Exhibition');
  const activeDbStatuses = new Set(otherKeys.flatMap(k => STATUS_GROUPS[k] || [k]));
  return activeDbStatuses.has(da.status);
}

function isApplicationNumberSearch(value) {
  const query = String(value || '').trim();
  return /\b(?:da\s*)?\d{4}[/.]\d{2,}(?:[/.]\d+)?\b/i.test(query) || /\bPAN-\d{5,}\b/i.test(query);
}


function buildFilterSummary(councils, stFilt, selectedCategories) {
  const councilSummary = councils.length === ALL_COUNCILS.length
    ? 'All councils'
    : councils.map(shortCouncilName).join(', ') || 'No councils';
  const statusSummary = stFilt.length === ALL_STATUS_KEYS.length
    ? 'All statuses'
    : stFilt.length === 0 ? 'No statuses'
    : stFilt.length <= 2 ? stFilt.join(', ')
    : `${stFilt.length} statuses`;
  const catSummary = selectedCategories.length === DA_CATEGORIES.length
    ? 'All categories'
    : selectedCategories.length === 0 ? 'No categories'
    : `${selectedCategories.length} of ${DA_CATEGORIES.length} categories`;
  return `${councilSummary} · ${statusSummary} · ${catSummary}`;
}

export default function Dashboard({
  loading, filt, filtTotal, search,
  rawSearch, setRawSearch,
  searchLoading, searchLoadingMore = false, searchMeta, isSearchMode, isPro,
  onLoadMoreSearch,
  st, stTrend, stPrev, period, zones, theme, setTab, setSelZone, zApps,
  setPeriod,
  councils, togC, allC, stFilt, setStFilt, allStFilt,
  selectedCategories, togCat, allCat,
  selectedPriceRanges, togPriceRange, clearPriceRanges,
  filtersCollapsed, setFiltersCollapsed,
  dashboardPipelineCollapsed = false,
  setDashboardPipelineCollapsed = () => {},
  dashboardPipelineOutcomesCollapsed = false,
  setDashboardPipelineOutcomesCollapsed = () => {},
  dashboardStatusCollapsed = false,
  setDashboardStatusCollapsed = () => {},
  dashboardWatchZonesCollapsed = false,
  setDashboardWatchZonesCollapsed = () => {},
  expId, setExpId, togTrack, isTr,
  fetchError, onRetry, lastUpdated,
  appsRetrying, usingCachedApps, onRefreshApp,
  pipelineStats,
  pipelineStatsLoading,
  onRetryPipelineStats,
  onShowOnMap,
  onOpenDA,
  scrollToExpandedOnArrive,
  clearScrollOnArrive,
  userEmail,
  userId,
  tracked = [],
}) {
  const [showProjects, setShowProjects] = useState(() => Boolean(rawSearch || expId));
  const projectHeadingRef = useRef(null);
  const findProjectsButtonRef = useRef(null);
  const openProjects = () => setShowProjects(true);
  const closeProjects = () => {
    setShowProjects(false);
    setSearchHelpOpen(false);
    setFilterDrawerOpen(false);
    requestAnimationFrame(() => findProjectsButtonRef.current?.focus());
  };
  useEffect(() => { if (expId) setShowProjects(true); }, [expId]);
  useEffect(() => {
    if (showProjects) projectHeadingRef.current?.focus();
  }, [showProjects]);
  const [displayCount, setDisplayCount] = useState(20);
  // Desktop view mode (cards | table) — localStorage for now; migrate to
  // user_preferences (column + checklist) when the desktop program lands fully.
  const isMobileViewport = useIsMobile(900);
  const [desktopViewMode, setDesktopViewModeState] = useState(() => {
    try {
      const saved = localStorage.getItem(`ss_desktop_view:${userId || 'guest'}`);
      return saved === 'cards' || saved === 'table' ? saved : 'cards';
    } catch { return 'cards'; }
  });
  const setDesktopViewMode = (mode) => {
    setDesktopViewModeState(mode);
    try { localStorage.setItem(`ss_desktop_view:${userId || 'guest'}`, mode); } catch { /* best effort */ }
  };
  const statsCollapsed = dashboardPipelineCollapsed === true;
  const pipelineOutcomesCollapsed = dashboardPipelineOutcomesCollapsed === true;
  const statusCollapsed = dashboardStatusCollapsed === true;
  const toggleStatsCollapsed = () => setDashboardPipelineCollapsed(!statsCollapsed);
  const togglePipelineOutcomesCollapsed = () => setDashboardPipelineOutcomesCollapsed(!pipelineOutcomesCollapsed);
  const toggleStatusCollapsed = () => setDashboardStatusCollapsed(!statusCollapsed);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [searchHelpOpen, setSearchHelpOpen] = useState(false);
  const [appTypeFilter, setAppTypeFilter] = useState('All');
  const [selectedTrade, setSelectedTrade] = useState('');
  const trade = TRADES.find(item => item.id === selectedTrade);
  const [dismissedOnboarding, setDismissedOnboarding] = useState(
    () => localStorage.getItem(`ss_onboarding_dismissed:${userId || 'guest'}`) === '1'
  );
  const [searchFocused, setSearchFocused] = useState(false);
  const searchInputRef = useRef(null);
  const searchScrollRef = useRef({ x: 0, y: 0, at: 0 });

  const rememberSearchScroll = () => {
    searchScrollRef.current = {
      x: window.scrollX || 0,
      y: window.scrollY || 0,
      at: Date.now(),
    };
  };

  // Reset pagination when filters or search change
  const councilKey = councils.join(',');
  const stFiltKey = stFilt.join(',');
  const catKey = selectedCategories.join(',');
  const safeRanges = Array.isArray(selectedPriceRanges) ? selectedPriceRanges : [];
  const priceKey = safeRanges.join(',');
  useEffect(() => { setDisplayCount(20); }, [councilKey, stFiltKey, catKey, priceKey, appTypeFilter, search, selectedTrade]);

  const handleDismissOnboarding = () => {
    setDismissedOnboarding(true);
    localStorage.setItem(`ss_onboarding_dismissed:${userId || 'guest'}`, '1');
  };

  // Ask SiteScope result → full DA detail view (fetches the row, then reuses
  // the standard onOpenDA path).
  const openDAById = async (daId) => {
    if (!daId || typeof onOpenDA !== 'function') return;
    try {
      const row = await fetchDADetailRow(daId);
      if (row) onOpenDA(row);
    } catch (err) {
      console.warn('[AskSiteScope] open result failed:', err?.message);
    }
  };

  // In browse mode SiteScopeApp has already applied council + status.
  // In search mode the Dashboard owns those visible filters so search narrows predictably.
  const filterBase = useMemo(() => {
    if (!isSearchMode) return filt;
    return filt.filter(a => matchesCouncilFilter(a, councils) && matchesStatusFilter(a, stFilt));
  }, [filt, isSearchMode, councilKey, stFiltKey]);

  // Category counts from pre-category-filtered list (council + status + search)
  const categoryCounts = useMemo(() => {
    const counts = {};
    for (const cat of DA_CATEGORIES) counts[cat.id] = 0;
    for (const da of filterBase) {
      const cat = resolveProposalType(da);
      if (counts[cat] !== undefined) counts[cat]++;
    }
    return counts;
  }, [filterBase]);

  const exactApplicationSearch = isSearchMode && isApplicationNumberSearch(search || rawSearch);

  // Apply category + application type filters for the display list.
  const filtCat = useMemo(() => {
    let result = (exactApplicationSearch || selectedTrade)
      ? filterBase
      : filterBase.filter(a => selectedCategories.includes(resolveProposalType(a)));
    if (exactApplicationSearch) return result;
    if (appTypeFilter === 'CDC') result = result.filter(a => CDC_TYPES.includes(a.application_type));
    if (appTypeFilter === 'DA')  result = result.filter(a => !CDC_TYPES.includes(a.application_type));
    return result;
  }, [filterBase, selectedCategories, appTypeFilter, exactApplicationSearch, selectedTrade]);

  // Apply price range filter after category filter.
  // DAs with no cost data are hidden when any price bracket is active.
  const filtPrice = useMemo(() => {
    if (exactApplicationSearch) return filtCat;
    if (safeRanges.length === 0) return filtCat;
    return filtCat.filter(a => {
      const cost = getPipelineCost(a);
      if (!cost) return false;
      return safeRanges.some(rangeId => {
        const range = PRICE_RANGE_DEFS.find(r => r.id === rangeId);
        return range && cost >= range.min && cost < range.max;
      });
    });
  }, [filtCat, priceKey, exactApplicationSearch]);

  const tradeMatches = useMemo(() => {
    const matches = new Map();
    if (!selectedTrade || exactApplicationSearch) return matches;
    for (const row of filtPrice) {
      const match = matchTradeOpportunity(row, selectedTrade);
      if (match) matches.set(row.id, match);
    }
    return matches;
  }, [filtPrice, selectedTrade, exactApplicationSearch]);
  const filtDisplay = useMemo(() => selectedTrade && !exactApplicationSearch
    ? filtPrice.filter(row => tradeMatches.has(row.id)) : filtPrice,
  [filtPrice, selectedTrade, exactApplicationSearch, tradeMatches]);

  // If expId targets a DA beyond the current page, expand displayCount to include it
  const expIdx = expId ? filtDisplay.findIndex(a => a.id === expId) : -1;
  const effectiveDisplayCount = (expIdx >= 0 && expIdx >= displayCount) ? expIdx + 1 : displayCount;
  const displayedApps = filtDisplay.slice(0, effectiveDisplayCount);
  const filteredValueSummary = useMemo(() => summarisePipelineValue(filtDisplay), [filtDisplay]);
  const showFilteredValueSummary = !loading && !searchLoading && filtDisplay.length > 0;
  const filteredValueLabel = isSearchMode ? 'Loaded application value' : 'Declared application value';
  const filteredValueScope = pipelineValueQualifier(filteredValueSummary);
  const relatedSites = useMemo(() => process.env.NODE_ENV === 'development' ? relatedAddressGroups(filtDisplay) : [], [filtDisplay]);

  const searchReturnedBeforeFilters = isSearchMode && Array.isArray(filt) ? filt.length : 0;
  const searchResultsHiddenByFilters = isSearchMode && !searchLoading && searchReturnedBeforeFilters > 0 && filtDisplay.length === 0;

  useLayoutEffect(() => {
    if (!searchFocused || document.activeElement !== searchInputRef.current) return;
    const snapshot = searchScrollRef.current;
    if (!snapshot.at || Date.now() - snapshot.at > 2500) return;
    requestAnimationFrame(() => {
      if (document.activeElement === searchInputRef.current) {
        window.scrollTo(snapshot.x, snapshot.y);
      }
    });
  }, [rawSearch, searchLoading, filtDisplay.length, searchFocused]);

  const searchTotalCount = Number(searchMeta?.total_count);
  const searchReturnedCount = Number(searchMeta?.returned_count);
  const searchSource = searchMeta?.source || null;
  const hasSearchCount = isSearchMode && !searchLoading && searchSource !== 'min_query' && Number.isFinite(searchTotalCount);
  const boundedReturnedCount = Number.isFinite(searchReturnedCount) ? searchReturnedCount : filtDisplay.length;
  const searchCountLabel = hasSearchCount
    ? searchTotalCount > boundedReturnedCount
      ? `Showing ${filtDisplay.length} of the top ${boundedReturnedCount} results. ${searchTotalCount} total matches.`
      : `Showing ${filtDisplay.length} result${filtDisplay.length !== 1 ? 's' : ''}. ${searchTotalCount} total match${searchTotalCount !== 1 ? 'es' : ''}.`
    : '';
  const searchSortDescription = searchMeta?.sort_description || 'Results are ranked by relevance, then newest lodged date.';
  const canLoadMoreSearch = Boolean(
    isSearchMode &&
    searchSource === 'search_das' &&
    searchMeta?.has_more &&
    typeof onLoadMoreSearch === 'function'
  );
  // Desktop table mode renders the whole filtered list, so card-style local
  // pagination doesn't apply there — without this the button is a no-op and
  // hides the real "Show more database results" server pagination.
  const isTableMode = !isMobileViewport && desktopViewMode === 'table';
  const showLocalMore = !loading && !isTableMode && displayCount < filtDisplay.length;
  const showSearchMore = !loading && !showLocalMore && canLoadMoreSearch;

  // Scroll the expanded DA into view.
  // - Suppress on initial mount (preserves scroll-to-top on tab return from Tracked DAs)
  // - UNLESS the mount is a navigate-and-reveal from Map "View Details"
  //   (scrollToExpandedOnArrive prop), in which case do scroll.
  const scrollTargetRef = useRef(null);
  const isInitialMount = useRef(true);
  useEffect(() => {
    const shouldScroll = expId && scrollTargetRef.current && (
      !isInitialMount.current || scrollToExpandedOnArrive
    );
    if (shouldScroll) {
      requestAnimationFrame(() => {
        scrollTargetRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
      // Consume the arrive flag so it doesn't fire again
      if (scrollToExpandedOnArrive && clearScrollOnArrive) {
        clearScrollOnArrive();
      }
    }
    isInitialMount.current = false;
  }, [expId, scrollToExpandedOnArrive]);

  return (
    <div style={{
      maxWidth: isMobileViewport ? "840px" : "none",
      width: "100%",
      boxSizing: "border-box",
      margin: "0 auto",
      padding: isMobileViewport ? "16px" : "18px 24px 24px",
    }}>
      <style>{`@keyframes shimmer { 0% { opacity: 0.3; } 50% { opacity: 0.6; } 100% { opacity: 0.3; } }`}</style>

      {/* Onboarding banner — shown to new users with no watch zones */}
      {!showProjects && zones.length === 0 && !loading && !dismissedOnboarding && (
        <div style={{background:`${theme.accent}15`,border:`1px solid ${theme.accent}40`,borderRadius:"10px",padding:"16px 20px",marginBottom:"16px",display:"flex",justifyContent:"space-between",alignItems:"center",gap:"12px"}}>
          <div>
            <div style={{color:theme.accent,fontWeight:"600",fontSize:"var(--text-base)",marginBottom:"4px"}}>Welcome to SiteScope</div>
            <div style={{color:theme.textMuted,fontSize:"var(--text-sm)"}}>See your regional overview below, then choose Find local projects to explore work in your area.</div>
          </div>
          <button onClick={handleDismissOnboarding} style={{background:"transparent",border:"none",color:theme.textMuted,fontSize:"var(--text-lg)",cursor:"pointer",padding:"4px 8px",flexShrink:0}}>×</button>
        </div>
      )}

      {/* Fetch error banner */}
      {fetchError && (
        <div style={{background:theme.bgCard,border:`1px solid ${theme.border}`,borderRadius:"10px",padding:"20px 16px",marginBottom:"16px",textAlign:"center"}}>
          <div style={{fontSize:"var(--text-base)",color:theme.text,marginBottom:"6px"}}>
            {usingCachedApps
              ? 'Live DA data is temporarily unavailable.'
              : appsRetrying
                ? 'Reconnecting to live DA data...'
                : 'Unable to load development applications.'}
          </div>
          <div style={{fontSize:"var(--text-sm)",color:theme.textMuted,marginBottom:"14px",lineHeight:1.45}}>
            {usingCachedApps
              ? `Showing the last saved dashboard${lastUpdated ? ` from ${lastUpdated.toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'})}` : ''}.`
              : 'Check your connection and try again. If this keeps happening, refresh the app shell.'}
          </div>
          <div style={{display:"flex",gap:10,justifyContent:"center",flexWrap:"wrap"}}>
            <button onClick={() => onRetry?.({ manual: true })} style={{background:"#c0a060",color:"#0a0f1a",border:"none",borderRadius:"6px",padding:"8px 20px",fontSize:"var(--text-sm)",fontWeight:"600",cursor:"pointer",fontFamily:"inherit"}}>{appsRetrying ? 'Retrying...' : 'Retry'}</button>
            <button onClick={onRefreshApp} style={{background:"transparent",color:theme.text,border:`1px solid ${theme.border}`,borderRadius:"6px",padding:"8px 14px",fontSize:"var(--text-sm)",fontWeight:"600",cursor:"pointer",fontFamily:"inherit"}}>Refresh app</button>
          </div>
        </div>
      )}

      <section aria-label="Dashboard overview" hidden={showProjects}>
        <DashboardPipelineSection
          period={period}
          setPeriod={setPeriod}
          pipelineStats={pipelineStats}
          pipelineStatsLoading={pipelineStatsLoading}
          onRetryPipelineStats={onRetryPipelineStats}
          lastUpdated={lastUpdated}
          theme={theme}
          collapsed={statsCollapsed}
          onToggle={toggleStatsCollapsed}
        />
        {/* Your Pipeline — personal deal-flow outcomes (paid tiers; renders nothing otherwise) */}
        <PipelineOutcomesCard
          theme={theme}
          userId={userId}
          tracked={tracked}
          onOpenTracked={() => setTab("tracked")}
          isPro={isPro}
          collapsed={pipelineOutcomesCollapsed}
          onCollapsedChange={togglePipelineOutcomesCollapsed}
        />

        <DashboardStatusSection
          loading={loading}
          st={st}
          councils={councils}
          theme={theme}
          collapsed={statusCollapsed}
          onToggle={toggleStatusCollapsed}
        />

        <button type="button" ref={findProjectsButtonRef} onClick={openProjects}
          style={{ width: '100%', padding: '16px 20px', marginTop: 8, background: theme.accent, color: '#0a0f1a', border: 'none', borderRadius: 10, fontFamily: 'inherit', fontSize: 18, fontWeight: 700, cursor: 'pointer' }}>
          Find local projects →
        </button>
      </section>
      <section aria-label="Find local projects" hidden={!showProjects}>
        <button type="button" onClick={closeProjects}
          style={{ background: 'transparent', color: theme.accent, border: 'none', padding: '8px 0', marginBottom: 12, fontFamily: 'inherit', fontSize: 14, cursor: 'pointer' }}>
          ← Back to dashboard
        </button>
      <h1 ref={projectHeadingRef} tabIndex={-1} style={{ color: theme.text, fontSize: 24, margin: '0 0 6px' }}>Find local projects</h1>
      <p style={{ color: theme.textMuted, margin: '0 0 16px', lineHeight: 1.5 }}>Choose your trade and search your area for potential work. Open a project to check its scope and next steps.</p>
      <TradePicker selectedTrade={selectedTrade} onChange={setSelectedTrade} theme={theme} />
      {/* Search + filter bar */}
      {/* Ask SiteScope — internal preview; renders nothing for everyone else */}


      <DashboardSearchControls
        selectedCategories={selectedCategories} councils={councils} stFilt={stFilt}
        selectedTrade={selectedTrade} safeRanges={safeRanges} appTypeFilter={appTypeFilter}
        rawSearch={rawSearch} isSearchMode={isSearchMode} theme={theme}
        searchFocused={searchFocused} setSearchHelpOpen={setSearchHelpOpen}
        searchInputRef={searchInputRef} loading={loading} searchLoading={searchLoading}
        resultCount={filtDisplay.length} rememberSearchScroll={rememberSearchScroll}
        setRawSearch={setRawSearch} setSearchFocused={setSearchFocused}
        setFilterDrawerOpen={setFilterDrawerOpen}
      />

      <SearchHelpModal
        open={searchHelpOpen}
        onClose={() => setSearchHelpOpen(false)}
        theme={theme}
      />

      {/* Active filter chips — shown when any filter narrows the default view */}
      <DashboardFilterChips
        selectedCategories={selectedCategories} councils={councils} togC={togC}
        stFilt={stFilt} setStFilt={setStFilt} selectedTrade={selectedTrade} allCat={allCat}
        trade={trade} setSelectedTrade={setSelectedTrade} safeRanges={safeRanges}
        togPriceRange={togPriceRange} appTypeFilter={appTypeFilter}
        setAppTypeFilter={setAppTypeFilter} theme={theme}
      />

      {hasSearchCount && (
        <div
          aria-label="Search result count and ranking"
          style={{
            color: theme.textMuted,
            fontSize: 11,
            lineHeight: 1.35,
            margin: "-2px 2px 10px",
          }}
        >
          {searchCountLabel} {searchSortDescription}
        </div>
      )}

      {showFilteredValueSummary && (
        <div
          aria-label="Filtered development value"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
            background: `${theme.bgCard}`,
            border: `1px solid ${theme.border}`,
            borderRadius: 8,
            padding: "9px 11px",
            margin: "-2px 2px 10px",
            boxShadow: "0 1px 6px rgba(0,0,0,0.18)",
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ color: theme.textMuted, fontSize: 10, fontWeight: 800, letterSpacing: 0.6, textTransform: "uppercase" }}>
              {filteredValueLabel}
            </div>
            <div style={{ color: theme.textMuted, fontSize: 11, lineHeight: 1.35, marginTop: 2 }}>
              {filteredValueScope}
              {isSearchMode && searchMeta?.has_more ? " loaded so far" : ""}
            </div>
          </div>
          <div style={{ color: filteredValueSummary.totalValue > 0 ? theme.accent : theme.textMuted, fontSize: 18, fontWeight: 850, whiteSpace: "nowrap" }}>
            {formatPipelineMoney(filteredValueSummary.totalValue)}
          </div>
        </div>
      )}

      {isSearchMode && searchSource === 'client_fallback' && (
        <div
          aria-label="Search fallback notice"
          style={{
            color: theme.textMuted,
            background: `${theme.accent}12`,
            border: `1px solid ${theme.accent}44`,
            borderRadius: 8,
            fontSize: 11,
            lineHeight: 1.35,
            padding: "8px 10px",
            margin: "-2px 2px 10px",
          }}
        >
          Showing quick results from your loaded feed. Full search is temporarily unavailable.
        </div>
      )}

      {/* FilterDrawer */}
      {filterDrawerOpen && (
        <FilterDrawer
          open={filterDrawerOpen}
          onClose={() => setFilterDrawerOpen(false)}
          councils={councils}
          togC={togC}
          allC={allC}
          stFilt={stFilt}
          togSt={setStFilt}
          allStFilt={allStFilt}
          selectedCategories={selectedCategories}
          togCat={togCat}
          allCat={allCat}
          selectedPriceRanges={selectedPriceRanges}
          togPriceRange={togPriceRange}
          clearPriceRanges={clearPriceRanges}
          appTypeFilter={appTypeFilter}
          setAppTypeFilter={setAppTypeFilter}
          selectedTrade={selectedTrade}
          onTradeChange={setSelectedTrade}
          resultCount={filtDisplay.length}
          isDesktopLayout={!isMobileViewport}
        />
      )}

      {process.env.NODE_ENV === 'development' && relatedSites.length > 0 && <section aria-label="Related application records" style={{ color: theme.textMuted, fontSize: 13, marginBottom: 12, overflowWrap: "anywhere" }}>
        <strong>Some records share an address.</strong> Values are summed per application, not unique projects or available contracts. Check related applications before adding budgets together.
        <ul>{relatedSites.slice(0, 5).map(group => <li key={group[0].id}>{group[0].address}: {group.map(row => row.council_application_number || row.da_number).join(' · ')}</li>)}</ul>
      </section>}
      {exactApplicationSearch && <p role="status" style={{ color: theme.textMuted }}>Reference lookup ignores trade, project category, application type and price filters. Council and status filters still apply.</p>}
      {trade && !exactApplicationSearch && !loading && !searchLoading && <p role="status" style={{ color: theme.textMuted, fontSize: 13, lineHeight: 1.5 }}>
        <strong style={{ color: theme.text }}>{filtDisplay.length.toLocaleString()} potential {trade.label.toLowerCase()} match{filtDisplay.length !== 1 ? 'es' : ''}</strong>
        {' · '}{isSearchMode ? 'Within these search results.' : 'From applications loaded for the past year.'} Matched from project scope across all categories. Confirm work availability with the project team.
      </p>}



      {/* DA list */}
      {/* Desktop view toggle — table is the office workhorse; cards stay default + mobile-only */}
      {!isMobileViewport && !loading && filtDisplay.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
          <div role="group" aria-label="Feed layout" style={{ display: 'inline-flex', border: `1px solid ${theme.border}`, borderRadius: 999, overflow: 'hidden' }}>
            {['cards', 'table'].map(mode => (
              <button
                key={mode}
                onClick={() => setDesktopViewMode(mode)}
                aria-pressed={desktopViewMode === mode}
                style={{
                  padding: '5px 14px', fontSize: 11, fontWeight: 700, fontFamily: 'inherit',
                  border: 'none', cursor: 'pointer', textTransform: 'capitalize',
                  background: desktopViewMode === mode ? `${theme.accent}22` : 'transparent',
                  color: desktopViewMode === mode ? theme.accent : theme.textMuted,
                }}
              >
                {mode}
              </button>
            ))}
          </div>
        </div>
      )}
      {!isMobileViewport && desktopViewMode === 'table' && !loading ? (
        <DATableView apps={filtDisplay} theme={theme} onOpenDA={onOpenDA} tradeMatches={tradeMatches} />
      ) : (
      <div style={{display:"flex",flexDirection:"column",gap:"8px"}}>
        {loading
          ? Array.from({length:5}).map((_,i)=>(
              <div key={i} style={{background:theme.bgCard,borderRadius:"10px",padding:"16px",border:`1px solid ${theme.border}`}}>
                <Skeleton width="120px" height="14px" bg={theme.border} />
                <div style={{height:"8px"}}/>
                <Skeleton width="70%" height="16px" bg={theme.border} />
                <div style={{height:"8px"}}/>
                <Skeleton width="40%" height="12px" bg={theme.border} />
              </div>
            ))
          : displayedApps.map((a,i)=>(
            <div key={a.id} ref={expId===a.id ? scrollTargetRef : null} {...(i===0?{'data-onboarding':'first-da-card'}:{})} style={i===0?{position:'relative'}:undefined}>
              <DACard app={a} exp={expId===a.id} onTog={()=>setExpId(expId===a.id?null:a.id)} onTrack={togTrack} tracked={isTr(a.id)} theme={theme} onShowOnMap={onShowOnMap} onOpenDA={onOpenDA} searchText={isSearchMode ? search : ''}/>
              {tradeMatches.has(a.id) && <p style={{ color: theme.accent, fontSize: 12, lineHeight: 1.5, margin: '6px 12px 12px' }}>{tradeMatches.get(a.id).tradeLabel} match: {tradeMatches.get(a.id).reason}</p>}
              {i===0 && <HelpTip id="da-card" position="top">Tap any DA to see full details, documents, and contacts.</HelpTip>}
            </div>
          ))
        }
      </div>
      )}
      {showLocalMore && (
        <button
          onClick={() => setDisplayCount(p => p + 20)}
          style={{width:"100%",padding:"12px",background:"transparent",border:`1px solid ${theme.border}`,borderRadius:"8px",color:theme.accent,fontSize:"var(--text-sm)",cursor:"pointer",fontFamily:"inherit",marginTop:"8px"}}
        >
          Show more ({filtDisplay.length - displayCount} remaining)
        </button>
      )}
      {showSearchMore && (
        <button
          onClick={onLoadMoreSearch}
          disabled={searchLoadingMore}
          style={{width:"100%",padding:"12px",background:"transparent",border:`1px solid ${theme.border}`,borderRadius:"8px",color:theme.accent,fontSize:"var(--text-sm)",cursor:searchLoadingMore?"default":"pointer",fontFamily:"inherit",marginTop:"8px",opacity:searchLoadingMore?0.7:1}}
        >
          {searchLoadingMore ? "Loading more..." : `Show more database results (${Math.max(0, searchTotalCount - boundedReturnedCount)} remaining)`}
        </button>
      )}
      {filtDisplay.length===0&&!loading&&!searchLoading&&(
        <div style={{textAlign:"center",padding:"40px",color:theme.textMuted}}>
          {isSearchMode && searchSource === 'client_fallback'
            ? <>Full search is temporarily unavailable. No matching DAs were found in the records already loaded. Clear search to browse or try again shortly.</>
            : isSearchMode && searchSource === 'min_query'
            ? <>Type at least {searchMeta?.min_length || 2} characters to search.</>
            : searchResultsHiddenByFilters
            ? (
              <>
                Search found {searchReturnedBeforeFilters} DA{searchReturnedBeforeFilters !== 1 ? 's' : ''} for <span style={{color:"#e8e0d8"}}>"{rawSearch}"</span>, but your current filters hide {searchReturnedBeforeFilters !== 1 ? 'them' : 'it'}. Clear or loosen the filters to view {searchReturnedBeforeFilters !== 1 ? 'them' : 'it'}.
              </>
            )
            : isSearchMode
            ? (() => {
                // Council label: null = all 4 selected (omit), short name if 1, count if 2–3
                const cl = councils.length < ALL_COUNCILS.length
                  ? (councils.length === 1
                      ? shortCouncilName(councils[0])
                      : `${councils.length} councils`)
                  : null;
                return (
                  <>
                    No DAs found for <span style={{color:"#e8e0d8"}}>"{rawSearch}"</span>
                    {cl ? ` across ${cl}` : ''}.{' '}
                    {isPro
                      ? "Check your spelling and active filters. A missing result does not mean no application exists."
                      : "Try a different search term."}
                  </>
                );
              })()
            : trade && !exactApplicationSearch
              ? `No ${trade.label.toLowerCase()} matches in these applications. Try a wider area, adjust your filters or clear the trade to browse all work.`
            : rawSearch
              ? <>No applications match <span style={{color:"#e8e0d8"}}>"{rawSearch}"</span>. Try a different search or clear the filter.</>
              : safeRanges.length > 0
                ? "No applications match your price filter. DAs without cost data are hidden when a price filter is active."
                : "No applications match your filters."
          }
        </div>
      )}
      </section>
      {canUseOutreachPacks(userEmail) && <details style={{ color: theme.textMuted, marginTop: 12 }}>
        <summary style={{ cursor: 'pointer', padding: '12px 0' }}>Advanced tools</summary>
        <AskSiteScope theme={theme} userEmail={userEmail} onOpenDAById={openDAById} />
      </details>}
      <div style={{borderTop:`1px solid ${theme.border}`,marginTop:"24px",padding:"12px 0",textAlign:"center",fontSize:"var(--text-xs)",color:theme.textMuted}}>
        DA data © State of New South Wales (Department of Planning, Housing and Infrastructure). Licensed under{' '}
        <a href="https://creativecommons.org/licenses/by/4.0/" target="_blank" rel="noopener noreferrer" style={{color:theme.textMuted,textDecoration:"underline"}}>Creative Commons Attribution</a>
        . Data has been transformed for display.
      </div>
    </div>
  );
}
